import { supabase } from '../lib/supabase';

class SupportTicketService {
  // Helper to get effective user ID (checks for impersonation)
  getEffectiveUserId() {
    try {
      const impersonationData = localStorage?.getItem('admin_impersonation');
      if (impersonationData) {
        const parsed = JSON.parse(impersonationData);
        console.log('[supportTicketService] Using impersonated user ID:', parsed?.impersonatedUserId);
        return parsed?.impersonatedUserId;
      }
    } catch (error) {
      console.error('[supportTicketService] Error reading impersonation data:', error);
    }
    return null;
  }

  async getAllTickets() {
    try {
      console.log('[supportTicketService] Getting current user...');
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      
      if (userError) {
        console.error('[supportTicketService] Auth error:', userError);
        throw userError;
      }
      
      if (!user) {
        console.log('[supportTicketService] No authenticated user');
        throw new Error('No authenticated user');
      }
      
      console.log('[supportTicketService] User authenticated:', user?.id);
      console.log('[supportTicketService] Fetching tickets...');
      
      const { data: tickets, error } = await supabase?.from('support_tickets')?.select(`
          *,
          user_profiles!support_tickets_user_id_fkey (
            full_name,
            email
          )
        `)?.order('created_at', { ascending: false });
      
      if (error) {
        console.error('[supportTicketService] Query error:', error);
        throw error;
      }
      
      console.log('[supportTicketService] Raw tickets data:', tickets);
      
      const formattedTickets = tickets?.map(ticket => ({
        id: ticket?.id,
        subject: ticket?.subject,
        description: ticket?.description,
        status: ticket?.status,
        urgency: ticket?.urgency,
        userId: ticket?.user_id,
        userName: ticket?.user_profiles?.full_name || 'Unknown User',
        userEmail: ticket?.user_profiles?.email || 'No Email',
        createdAt: ticket?.created_at,
        updatedAt: ticket?.updated_at,
        closedAt: ticket?.closed_at,
        closedBy: ticket?.closed_by
      }));
      
      console.log('[supportTicketService] Formatted tickets:', formattedTickets);
      return formattedTickets || [];
    } catch (error) {
      console.error('[supportTicketService] Error in getAllTickets:', error);
      throw error;
    }
  }

  async getUserTickets() {
    try {
      console.log('[supportTicketService] Getting user tickets...');
      
      // Check for impersonation first
      const impersonatedUserId = this.getEffectiveUserId();
      
      if (impersonatedUserId) {
        // Admin is impersonating - fetch tickets for impersonated user
        console.log('[supportTicketService] Fetching tickets for impersonated user:', impersonatedUserId);
        
        const { data: tickets, error } = await supabase
          ?.from('support_tickets')
          ?.select('*')
          ?.eq('user_id', impersonatedUserId)
          ?.order('created_at', { ascending: false });
        
        if (error) {
          console.error('[supportTicketService] Query error:', error);
          throw error;
        }
        
        console.log('[supportTicketService] Raw impersonated user tickets:', tickets);
        
        const formattedTickets = tickets?.map(ticket => ({
          id: ticket?.id,
          subject: ticket?.subject,
          description: ticket?.description,
          status: ticket?.status,
          urgency: ticket?.urgency,
          userId: ticket?.user_id,
          createdAt: ticket?.created_at,
          updatedAt: ticket?.updated_at,
          closedAt: ticket?.closed_at,
          closedBy: ticket?.closed_by
        }));
        
        console.log('[supportTicketService] Formatted impersonated user tickets:', formattedTickets);
        return formattedTickets || [];
      }
      
      // Normal flow - not impersonating
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      
      if (userError) {
        console.error('[supportTicketService] Auth error:', userError);
        throw userError;
      }
      
      if (!user) {
        console.log('[supportTicketService] No authenticated user');
        throw new Error('No authenticated user');
      }
      
      console.log('[supportTicketService] User authenticated:', user?.id);
      console.log('[supportTicketService] Fetching user-specific tickets...');
      
      const { data: tickets, error } = await supabase
        ?.from('support_tickets')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false });
      
      if (error) {
        console.error('[supportTicketService] Query error:', error);
        throw error;
      }
      
      console.log('[supportTicketService] Raw user tickets data:', tickets);
      
      const formattedTickets = tickets?.map(ticket => ({
        id: ticket?.id,
        subject: ticket?.subject,
        description: ticket?.description,
        status: ticket?.status,
        urgency: ticket?.urgency,
        userId: ticket?.user_id,
        createdAt: ticket?.created_at,
        updatedAt: ticket?.updated_at,
        closedAt: ticket?.closed_at,
        closedBy: ticket?.closed_by
      }));
      
      console.log('[supportTicketService] Formatted user tickets:', formattedTickets);
      return formattedTickets || [];
    } catch (error) {
      console.error('[supportTicketService] Error in getUserTickets:', error);
      throw error;
    }
  }

  async createTicket(subject, description, urgency) {
    try {
      console.log('[supportTicketService] Creating ticket...');
      
      // Check for impersonation
      const impersonatedUserId = this.getEffectiveUserId();
      
      if (impersonatedUserId) {
        // Creating ticket as impersonated user
        console.log('[supportTicketService] Creating ticket for impersonated user:', impersonatedUserId);
        
        const { data, error } = await supabase
          ?.from('support_tickets')
          ?.insert([
            {
              user_id: impersonatedUserId,
              subject: subject,
              description: description,
              urgency: urgency,
              status: 'open'
            }
          ])
          ?.select()
          ?.single();

        if (error) {
          console.error('[supportTicketService] Create ticket error:', error);
          throw error;
        }

        console.log('[supportTicketService] Ticket created successfully for impersonated user:', data);
        return data;
      }
      
      // Normal flow - not impersonating
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      
      if (userError) {
        console.error('[supportTicketService] Auth error:', userError);
        throw userError;
      }
      
      if (!user) {
        console.log('[supportTicketService] No authenticated user');
        throw new Error('No authenticated user');
      }

      const { data, error } = await supabase
        ?.from('support_tickets')
        ?.insert([
          {
            user_id: user?.id,
            subject: subject,
            description: description,
            urgency: urgency,
            status: 'open'
          }
        ])
        ?.select()
        ?.single();

      if (error) {
        console.error('[supportTicketService] Create ticket error:', error);
        throw error;
      }

      console.log('[supportTicketService] Ticket created successfully:', data);
      return data;
    } catch (error) {
      console.error('[supportTicketService] Error creating ticket:', error);
      throw error;
    }
  }

  async updateTicketStatus(ticketId, newStatus) {
    try {
      const updates = {
        status: newStatus,
        updated_at: new Date()?.toISOString()
      };

      if (newStatus === 'closed') {
        const { data: { user } } = await supabase?.auth?.getUser();
        updates.closed_at = new Date()?.toISOString();
        updates.closed_by = user?.id;
      }

      const { data, error } = await supabase?.from('support_tickets')?.update(updates)?.eq('id', ticketId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating ticket status:', error);
      throw error;
    }
  }

  async deleteTicket(ticketId) {
    try {
      const { error } = await supabase?.from('support_tickets')?.delete()?.eq('id', ticketId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting ticket:', error);
      throw error;
    }
  }

  async getTicketMessages(ticketId) {
    try {
      console.log('[supportTicketService] Fetching messages for ticket:', ticketId);
      
      const { data: messages, error } = await supabase
        ?.from('ticket_messages')
        ?.select(`
          *,
          user_profiles!ticket_messages_sender_id_fkey (
            full_name,
            email,
            role
          )
        `)
        ?.eq('ticket_id', ticketId)
        ?.order('created_at', { ascending: true });
      
      if (error) {
        console.error('[supportTicketService] Query error:', error);
        throw error;
      }
      
      console.log('[supportTicketService] Raw messages data:', messages);
      
      const formattedMessages = messages?.map(msg => ({
        id: msg?.id,
        ticketId: msg?.ticket_id,
        senderId: msg?.sender_id,
        senderName: msg?.is_admin 
          ? 'Admin' : (msg?.user_profiles?.full_name ||'User'),
        senderEmail: msg?.user_profiles?.email || 'No Email',
        senderRole: msg?.user_profiles?.role || 'user',
        message: msg?.message,
        isAdmin: msg?.is_admin,
        createdAt: msg?.created_at,
        updatedAt: msg?.updated_at
      }));
      
      console.log('[supportTicketService] Formatted messages:', formattedMessages);
      return formattedMessages || [];
    } catch (error) {
      console.error('[supportTicketService] Error in getTicketMessages:', error);
      throw error;
    }
  }

  async createTicketMessage(ticketId, message) {
    try {
      console.log('[supportTicketService] Creating message for ticket:', ticketId);
      
      // Check for impersonation
      const impersonatedUserId = this.getEffectiveUserId();
      let userId;
      let isAdmin = false;
      
      if (impersonatedUserId) {
        // Admin is impersonating - message from impersonated user
        userId = impersonatedUserId;
        console.log('[supportTicketService] Creating message for impersonated user:', userId);
      } else {
        // Get actual authenticated user
        const { data: { user }, error: userError } = await supabase?.auth?.getUser();
        
        if (userError) {
          console.error('[supportTicketService] Auth error:', userError);
          throw userError;
        }
        
        if (!user) {
          console.log('[supportTicketService] No authenticated user');
          throw new Error('No authenticated user');
        }
        
        userId = user?.id;
        
        // Check if user is admin
        const { data: profile, error: profileError } = await supabase
          ?.from('user_profiles')
          ?.select('role')
          ?.eq('id', userId)
          ?.single();
        
        if (!profileError && profile) {
          isAdmin = profile?.role === 'admin';
        }
      }

      const { data, error } = await supabase
        ?.from('ticket_messages')
        ?.insert([
          {
            ticket_id: ticketId,
            sender_id: userId,
            message: message,
            is_admin: isAdmin
          }
        ])
        ?.select()
        ?.single();

      if (error) {
        console.error('[supportTicketService] Create message error:', error);
        throw error;
      }

      console.log('[supportTicketService] Message created successfully:', data);
      return data;
    } catch (error) {
      console.error('[supportTicketService] Error creating message:', error);
      throw error;
    }
  }

  // Subscribe to real-time message updates
  subscribeToTicketMessages(ticketId, callback) {
    console.log('[supportTicketService] Setting up real-time subscription for ticket:', ticketId);
    
    const subscription = supabase
      ?.channel(`ticket_messages:${ticketId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ticket_messages',
          filter: `ticket_id=eq.${ticketId}`
        },
        async (payload) => {
          console.log('[supportTicketService] New message received:', payload);
          
          // Fetch full message with user profile data
          const { data: message, error } = await supabase
            ?.from('ticket_messages')
            ?.select(`
              *,
              user_profiles!ticket_messages_sender_id_fkey (
                full_name,
                email,
                role
              )
            `)
            ?.eq('id', payload?.new?.id)
            ?.single();
          
          if (!error && message) {
            const formattedMessage = {
              id: message?.id,
              ticketId: message?.ticket_id,
              senderId: message?.sender_id,
              senderName: message?.is_admin 
                ? 'Admin' : (message?.user_profiles?.full_name ||'User'),
              senderEmail: message?.user_profiles?.email || 'No Email',
              senderRole: message?.user_profiles?.role || 'user',
              message: message?.message,
              isAdmin: message?.is_admin,
              createdAt: message?.created_at,
              updatedAt: message?.updated_at
            };
            
            callback(formattedMessage);
          }
        }
      )
      ?.subscribe();
    
    return subscription;
  }

  // Unsubscribe from real-time updates
  unsubscribeFromTicketMessages(subscription) {
    if (subscription) {
      console.log('[supportTicketService] Unsubscribing from real-time updates');
      supabase?.removeChannel(subscription);
    }
  }

  // Check for unread messages (messages from admin that user hasn't seen)
  async getUnreadMessageCount() {
    try {
      console.log('[supportTicketService] Checking for unread messages...');
      
      // Check for impersonation
      const impersonatedUserId = this.getEffectiveUserId();
      let userId;
      
      if (impersonatedUserId) {
        userId = impersonatedUserId;
      } else {
        const { data: { user }, error: userError } = await supabase?.auth?.getUser();
        if (userError || !user) {
          return 0;
        }
        userId = user?.id;
      }

      // Get all open/in_progress tickets for this user with their last viewed time
      const { data: tickets, error: ticketsError } = await supabase
        ?.from('support_tickets')
        ?.select(`
          id, 
          updated_at,
          ticket_views!left(last_viewed_at)
        `)
        ?.eq('user_id', userId)
        ?.in('status', ['open', 'in_progress']);
      
      if (ticketsError || !tickets || tickets?.length === 0) {
        return 0;
      }

      let unreadCount = 0;

      // For each ticket, check if there are admin messages newer than last view
      for (const ticket of tickets) {
        const lastViewedAt = ticket?.ticket_views?.[0]?.last_viewed_at;
        
        // If ticket has never been viewed, check for any admin messages
        if (!lastViewedAt) {
          const { data: adminMessages, error: messagesError } = await supabase
            ?.from('ticket_messages')
            ?.select('id')
            ?.eq('ticket_id', ticket?.id)
            ?.eq('is_admin', true)
            ?.limit(1);
          
          if (!messagesError && adminMessages && adminMessages?.length > 0) {
            unreadCount++;
          }
        } else {
          // Check for admin messages after last view
          const { data: newAdminMessages, error: messagesError } = await supabase
            ?.from('ticket_messages')
            ?.select('id')
            ?.eq('ticket_id', ticket?.id)
            ?.eq('is_admin', true)
            ?.gt('created_at', lastViewedAt)
            ?.limit(1);
          
          if (!messagesError && newAdminMessages && newAdminMessages?.length > 0) {
            unreadCount++;
          }
        }
      }

      console.log('[supportTicketService] Unread message count:', unreadCount);
      return unreadCount;
    } catch (error) {
      console.error('[supportTicketService] Error in getUnreadMessageCount:', error);
      return 0;
    }
  }

  // Mark ticket as viewed by current user
  async markTicketAsViewed(ticketId) {
    try {
      console.log('[supportTicketService] Marking ticket as viewed:', ticketId);
      
      // Check for impersonation
      const impersonatedUserId = this.getEffectiveUserId();
      let userId;
      
      if (impersonatedUserId) {
        userId = impersonatedUserId;
      } else {
        const { data: { user }, error: userError } = await supabase?.auth?.getUser();
        if (userError || !user) {
          throw new Error('Not authenticated');
        }
        userId = user?.id;
      }

      // Upsert the ticket view record
      const { data, error } = await supabase
        ?.from('ticket_views')
        ?.upsert(
          {
            ticket_id: ticketId,
            user_id: userId,
            last_viewed_at: new Date()?.toISOString()
          },
          {
            onConflict: 'ticket_id,user_id'
          }
        )
        ?.select()
        ?.single();

      if (error) {
        console.error('[supportTicketService] Error marking ticket as viewed:', error);
        throw error;
      }

      console.log('[supportTicketService] Ticket marked as viewed successfully:', data);
      return data;
    } catch (error) {
      console.error('[supportTicketService] Error in markTicketAsViewed:', error);
      throw error;
    }
  }

  // Subscribe to new admin messages for notification purposes
  subscribeToNewAdminMessages(callback) {
    console.log('[supportTicketService] Setting up notification subscription for admin messages');
    
    const subscription = supabase
      ?.channel('admin_message_notifications')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ticket_messages',
          filter: 'is_admin=eq.true'
        },
        (payload) => {
          console.log('[supportTicketService] New admin message notification:', payload);
          callback(payload?.new);
        }
      )
      ?.subscribe();
    
    return subscription;
  }
}

export const supportTicketService = new SupportTicketService();